/**
 * DecompiledCodeViewer — shows decompiled Java classes with syntax highlighting
 * and suspicious lines marked.
 */

import { useState } from 'react'
import SyntaxHighlighter from 'react-syntax-highlighter'
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import type { DecompiledClass } from '../types/scan'
import { AlertTriangle, Code2, ChevronRight, ChevronDown } from 'lucide-react'
import styles from './DecompiledCodeViewer.module.css'

interface Props {
  classes: DecompiledClass[]
}

export default function DecompiledCodeViewer({ classes }: Props) {
  const [selectedClass, setSelectedClass] = useState<string | null>(
    classes.find((c) => c.suspicious)?.name ?? classes[0]?.name ?? null
  )
  const [showAll, setShowAll] = useState(false)

  const suspiciousClasses = classes.filter((c) => c.suspicious)
  const otherClasses = classes.filter((c) => !c.suspicious)

  const displayClasses = showAll
    ? classes
    : [...suspiciousClasses, ...otherClasses.slice(0, 5)]

  const selected = classes.find((c) => c.name === selectedClass)

  if (classes.length === 0) {
    return (
      <div className={styles.empty}>
        <Code2 size={20} />
        <span>Brak zdekompilowanych klas</span>
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <h2 className={styles.title}>
        <Code2 size={18} />
        Kod źródłowy
        <span className={styles.counts}>
          {suspiciousClasses.length > 0 && (
            <span className={styles.suspiciousCount}>
              {suspiciousClasses.length} podejrzanych
            </span>
          )}
          <span className={styles.totalCount}>{classes.length} klas łącznie</span>
        </span>
      </h2>

      <div className={styles.layout}>
        {/* ── Class list sidebar ─────────────────────────────────────────── */}
        <div className={styles.sidebar}>
          {displayClasses.map((c) => (
            <button
              key={c.name}
              className={`${styles.classBtn} ${selectedClass === c.name ? styles.classBtnActive : ''} ${c.suspicious ? styles.classBtnSuspicious : ''}`}
              onClick={() => setSelectedClass(c.name)}
              title={c.name}
            >
              {c.suspicious ? (
                <AlertTriangle size={12} className={styles.suspiciousIcon} />
              ) : (
                <ChevronRight size={12} className={styles.normalIcon} />
              )}
              <span className={styles.classSimpleName}>{c.simpleName}</span>
            </button>
          ))}

          {!showAll && otherClasses.length > 5 && (
            <button className={styles.showMoreBtn} onClick={() => setShowAll(true)}>
              <ChevronDown size={12} />
              Pokaż jeszcze {otherClasses.length - 5} klas
            </button>
          )}
        </div>

        {/* ── Code viewer ───────────────────────────────────────────────── */}
        <div className={styles.codePane}>
          {selected ? (
            <>
              <div className={styles.codeHeader}>
                <code className={styles.codePath}>{selected.name}.java</code>
                {selected.suspicious && (
                  <span className={styles.suspiciousLabel}>
                    <AlertTriangle size={12} />
                    Podejrzana klasa
                  </span>
                )}
              </div>
              <div className={styles.codeWrapper}>
                <SyntaxHighlighter
                  language="java"
                  style={atomOneDark}
                  showLineNumbers
                  wrapLines
                  lineProps={(lineNumber) => {
                    const isSuspicious =
                      selected.suspiciousLines?.includes(lineNumber) ?? false
                    return {
                      style: isSuspicious
                        ? {
                            display: 'block',
                            backgroundColor: 'rgba(239, 68, 68, 0.12)',
                            borderLeft: '3px solid #ef4444',
                          }
                        : { display: 'block' },
                    }
                  }}
                  customStyle={{
                    background: 'transparent',
                    fontSize: '0.8rem',
                    lineHeight: '1.6',
                    margin: 0,
                    padding: '16px',
                  }}
                >
                  {selected.source}
                </SyntaxHighlighter>
              </div>
            </>
          ) : (
            <div className={styles.noSelection}>
              <Code2 size={24} />
              <span>Wybierz klasę, aby zobaczyć jej kod źródłowy</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
